import { useState } from 'preact/hooks';
import { api } from '../api.js';
import {
  goBack,
  replace,
  flow,
  currentTask,
  skipsThisSession,
  refreshCurrentTask,
  showToast,
  friendlyError,
  bumpTasksVersion,
} from '../store.js';
import { __ } from '../i18n.js';
import { SKIP_REASONS } from '../lib/labels.js';
import BackButton from '../components/BackButton.jsx';
import Icon from '../components/Icon.jsx';

// Roadmap §6: after 2 skips in one sitting, offer a gentle intervention
// instead of just handing back another task at the same time budget.
const INTERVENTION_THRESHOLD = 2;

/**
 * Reason-consequence table (roadmap §6). Every reason still records the
 * skip server-side; this screen only needs to react to what the response
 * asks for next:
 *  - 'materials' needs a short note first (what to buy) before submitting.
 *  - 'time' comes back with an already-computed smaller-budget suggestion —
 *    show it directly instead of re-fetching at the old (too large) budget.
 *  - 'help' may come back with household members to ask — hand off to a
 *    dedicated screen instead of just going back.
 *  - everything else just re-fetches the next task for the same category/
 *    budget, same as before Phase 4.
 */
export default function SkipScreen({ params }) {
  const [submitting, setSubmitting] = useState(false);
  const [showMaterialsForm, setShowMaterialsForm] = useState(false);
  const [materialsNote, setMaterialsNote] = useState('');

  async function handleSkip(reason, note) {
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await api.tasks.skip(params.taskId, {
        reason,
        note,
        available_minutes: flow.value.availableMinutes,
      });
      skipsThisSession.value = skipsThisSession.value + 1;
      // WP-2: every reason-branch below already skipped the task
      // server-side by this point, so the desktop Aankomend panel needs a
      // refetch regardless of which branch runs next.
      bumpTasksVersion();

      if (reason === 'time' && res.retry) {
        flow.value = { ...flow.value, availableMinutes: res.available_minutes };
        currentTask.value = res.retry;
        showToast(__('Opnieuw gekozen met minder tijd', 'etm'), 'info');
        goBack();
        return;
      }

      if (reason === 'help' && res.household_members && res.household_members.length > 0) {
        showToast(__('Taak overgeslagen', 'etm'), 'info');
        replace('assignHelp', { taskId: params.taskId, members: res.household_members });
        return;
      }

      await refreshCurrentTask();
      showToast(__('Taak overgeslagen', 'etm'), 'info');

      if (skipsThisSession.value >= INTERVENTION_THRESHOLD) {
        replace('intervention');
      } else {
        goBack();
      }
    } catch (err) {
      showToast(friendlyError(err), 'error');
    } finally {
      setSubmitting(false);
    }
  }

  function pickReason(reason) {
    if (submitting) return;
    if (reason === 'materials') {
      setShowMaterialsForm(true);
      return;
    }
    handleSkip(reason);
  }

  if (showMaterialsForm) {
    return (
      <div>
        <BackButton onClick={() => setShowMaterialsForm(false)} />
        <h1 class="etm-screen-title">{__('Wat heb je nodig?', 'etm')}</h1>
        <p class="etm-screen-subtitle">
          {__('We maken hier een quick win van om dat eerst te regelen.', 'etm')}
        </p>

        <div class="etm-form-group">
          <input
            type="text"
            class="etm-form-input"
            value={materialsNote}
            disabled={submitting}
            onInput={(e) => setMaterialsNote(e.currentTarget.value)}
            placeholder={__('Bijv. verf, schroeven…', 'etm')}
            maxLength={140}
            autoComplete="off"
          />
        </div>

        <button
          type="button"
          class="etm-btn etm-btn-primary"
          disabled={submitting}
          onClick={() => handleSkip('materials', materialsNote.trim())}
        >
          {submitting ? __('Bezig…', 'etm') : __('Overslaan', 'etm')}
        </button>
      </div>
    );
  }

  return (
    <div>
      <BackButton onClick={goBack} />
      <h1 class="etm-screen-title">{__('Waarom niet?', 'etm')}</h1>
      <p class="etm-screen-subtitle">{__('Selecteer een reden', 'etm')}</p>

      <div class="etm-skip-reasons">
        {SKIP_REASONS.map((r) => (
          <button
            key={r.value}
            type="button"
            class="etm-skip-reason-btn"
            disabled={submitting}
            onClick={() => pickReason(r.value)}
          >
            <span class="icon">
              <Icon name={r.icon} size={22} />
            </span>
            <span class="label">{r.label()}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
